import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

export type InputType = 'text' | 'email' | 'password' | 'number' | 'date' | 'time' | 'search' | 'tel' | 'url';
export type InputAppearance = 'outline' | 'fill';
export type InputMode = 'view' | 'edit';

@Component({
  selector: 'app-form-input',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
  standalone: false,
})
export class FormInputComponent implements OnInit, OnChanges {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() inputType: InputType = 'text';
  @Input() appearance: InputAppearance = 'outline';
  @Input() formControl: FormControl | null = null;
  @Input() formGroup: FormGroup | null = null;
  @Input() controlName: string = '';
  @Input() errorMessage: string = '';
  @Input() hint: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() rows: number = 4;
  @Input() id: string = '';
  @Input() min?: string | number;
  @Input() max?: string | number;
  @Input() step?: string | number;
  @Input() useMaterial: boolean = true;
  @Input() isTextarea: boolean = false;
  @Input() mode: InputMode = 'edit';
  @Input() allowViewMode: boolean = false;

  @Output() valueChange = new EventEmitter<any>();
  @Output() blur = new EventEmitter<void>();
  @Output() focus = new EventEmitter<void>();
  @Output() modeChange = new EventEmitter<InputMode>();

  currentMode: InputMode = 'view';
  isFocused: boolean = false;

  ngOnInit() {
    this.currentMode = this.mode;
  }

  ngOnChanges() {
    this.currentMode = this.mode;
  }


  get control(): FormControl | null {
    if (this.formControl) {
      return this.formControl;
    }
    if (this.formGroup && this.controlName) {
      return this.formGroup.get(this.controlName) as FormControl;
    }
    return null;
  }

  get isInvalid(): boolean {
    return this.control ? this.control.invalid && this.control.touched : false;
  }

  get displayError(): string {
    if (!this.isInvalid) return '';
    
    if (this.errorMessage) return this.errorMessage;

    const errors = this.control?.errors;
    if (!errors) return '';

    if (errors['required']) return `${this.label || 'This field'} is required`;
    if (errors['email']) return 'Please enter a valid email address';
    if (errors['minlength']) return `Minimum length is ${errors['minlength'].requiredLength} characters`;
    if (errors['maxlength']) return `Maximum length is ${errors['maxlength'].requiredLength} characters`;
    if (errors['min']) return `Minimum value is ${errors['min'].min}`;
    if (errors['max']) return `Maximum value is ${errors['max'].max}`;
    if (errors['pattern']) return 'Please enter a valid value';

    return 'Invalid input';
  }

  onValueChange(value: any) {
    this.valueChange.emit(value);
  }

  onBlur() {
    this.isFocused = false;
    if (this.control) {
      this.control.markAsTouched();
    }
    this.blur.emit();
  }

  onFocus() {
    this.isFocused = true;
    this.focus.emit();
  }

  switchToEditMode() {
    this.currentMode = 'edit';
    this.modeChange.emit('edit');
  }

  switchToViewMode() {
    this.currentMode = 'view';
    this.modeChange.emit('view');
  }

  getDisplayValue(): string {
    const value = this.control?.value;
    if (!value) return '—';
    if (this.inputType === 'date' && value) {
      return new Date(value).toLocaleDateString();
    }
    return String(value);
  }
}
